import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import { api } from "../api";
import "./SalaryView.css";

const MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const MONTH_ABBR: Record<string,string> = {
  April:"Apr",May:"May",June:"Jun",July:"Jul",August:"Aug",September:"Sep",
  October:"Oct",November:"Nov",December:"Dec",January:"Jan",February:"Feb",March:"Mar",
};
const ALL_MONTH_OPTIONS = MONTHS.map((m, i) => ({ label: MONTH_ABBR[m], value: i + 1 }));

const DIMENSION_START_MONTH: Record<string, number> = {
  "July Forecast": 3,
  "October Forecast": 6,
  "January Forecast": 9,
};

interface Employee {
  id?: number;
  cost_center_number: string;
  employee_name: string;
  job_title: string;
  annual_salary: number; // derived: hourly_rate * hours_per_week * 52
  employment_type: "Full-Time" | "Part-Time" | "Temp";
  bip_pct: number;
  hire_month: number;
  status: "active" | "terminated";
  termination_month: number;
  contract_end_month: number; // 0 = open-ended, 1-12 = planned end fiscal month
  hours_per_week: number;     // hours worked per week
  is_new_hire?: boolean;
}

interface TempLaborViewProps {
  costCenterNumber: string;
  onDataSaved?: () => void;
}

type ContractType = "Hourly" | "Turnkey";

function computeMonthly(
  annualSalary: number,
  hire_month = 0,
  contract_end_month = 0,
  status: string = "active",
  termination_month = 0,
  contractType: ContractType = "Hourly"
): Record<string, number> {
  const result: Record<string, number> = {};
  if (contractType === "Turnkey") {
    let activeCount = 0;
    MONTHS.forEach((_, i) => {
      const fm = i + 1;
      const start = hire_month > 0 ? hire_month : 1;
      const end = (status === "terminated" && termination_month > 0)
        ? termination_month
        : (contract_end_month > 0 ? contract_end_month : 12);
      if (fm >= start && fm <= end) activeCount++;
    });
    const perMonth = activeCount > 0 ? Math.round(annualSalary / activeCount) : 0;
    MONTHS.forEach((m, i) => {
      const fm = i + 1;
      const start = hire_month > 0 ? hire_month : 1;
      const end = (status === "terminated" && termination_month > 0)
        ? termination_month
        : (contract_end_month > 0 ? contract_end_month : 12);
      result[m] = (fm >= start && fm <= end) ? perMonth : 0;
    });
  } else {
    MONTHS.forEach((m, i) => {
      const fm = i + 1;
      if (hire_month > 0 && fm < hire_month) {
        result[m] = 0;
      } else if (contract_end_month > 0 && fm > contract_end_month) {
        result[m] = 0;
      } else if (status === "terminated" && termination_month > 0 && fm > termination_month) {
        result[m] = 0;
      } else {
        result[m] = Math.round(annualSalary / 12);
      }
    });
  }
  return result;
}

const fmt = (n: number) =>
  n === 0 ? "–" : "$" + Math.round(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const emptyContractor = (cc: string): Employee => ({
  cost_center_number: cc,
  employee_name: "",
  job_title: "",
  annual_salary: 0,
  employment_type: "Temp",
  bip_pct: 0,
  hire_month: 0,
  status: "active",
  termination_month: 0,
  contract_end_month: 0,
  hours_per_week: 40,
  is_new_hire: false,
});

/** Derive hourly rate from stored annual_salary and hours_per_week */
const getHourlyRate = (emp: Employee): number => {
  const hpw = emp.hours_per_week || 40;
  return emp.annual_salary > 0 ? Math.round(emp.annual_salary / (hpw * 52) * 100) / 100 : 0;
};

/** Derive annual_salary from hourly rate and hours/week */
const toAnnual = (hourlyRate: number, hoursPerWeek: number): number =>
  Math.round(hourlyRate * hoursPerWeek * 52);

const emptyNewContractor = (cc: string, firstMonth: number): Employee => ({
  ...emptyContractor(cc),
  hire_month: firstMonth,
  is_new_hire: true,
});

const TempLaborView: React.FC<TempLaborViewProps> = ({ costCenterNumber, onDataSaved }) => {
  const [existing, setExisting] = useState<Employee[]>([]);
  const [newHires, setNewHires] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [forecastDim, setForecastDim] = useState<string | null>(null);
  const [forecastActive, setForecastActive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ section: "existing" | "newHire"; idx: number; name: string } | null>(null);
  const [tempChartType, setTempChartType] = useState<"bar" | "line">("bar");
  const [tempChartOpen, setTempChartOpen] = useState(false);
  const [actualsMonthly, setActualsMonthly] = useState<Record<string, number>>({});
  const [dragSource, setDragSource] = useState<{ section: "existing" | "newHire"; idx: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<"existing" | "newHire" | null>(null);

  // Snapshot for dirty detection
  const savedSnapshot = useRef<string>("");
  const serializeEmployees = (ex: Employee[], nh: Employee[]) =>
    JSON.stringify([...ex.map(e => ({ ...e })), ...nh]);
  const isDirty = !loading && serializeEmployees(existing, newHires) !== savedSnapshot.current;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const empRes = await api.get(`/salary/employees?cost_center=${costCenterNumber}`);
      const all: Employee[] = empRes.data.map((e: any) => ({
        ...e,
        status: e.status || "active",
        termination_month: e.termination_month || 0,
        contract_end_month: e.contract_end_month || 0,
        hours_per_week: e.hours_per_week ?? 0,
      }));
      const temps = all.filter(e => e.employment_type === "Temp");
      const ex = temps.filter(e => !e.is_new_hire);
      const nh = temps.filter(e => !!e.is_new_hire);
      setExisting(ex);
      setNewHires(nh);
      savedSnapshot.current = serializeEmployees(ex, nh);
    } catch {
      setFeedback({ ok: false, msg: "Failed to load contractor data." });
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        setForecastActive(!!res.data.active);
        if (res.data.dimension) setForecastDim(res.data.dimension);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/settings/fiscal-year")
      .then(fyRes => {
        const fy = fyRes.data?.fiscalYear || "FY26";
        return api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: "Actuals", fy } });
      })
      .then(res => {
        const data = res.data as Record<string, any[]>;
        const totals: Record<string, number> = {};
        MONTHS.forEach(m => { totals[m] = 0; });
        for (const [acctKey, entries] of Object.entries(data)) {
          if (!/^(60770|60020)\b/.test(acctKey)) continue;
          for (const e of entries) {
            if (e.month && MONTHS.includes(e.month)) {
              totals[e.month] += Number(e.budget) || 0;
            }
          }
        }
        setActualsMonthly(totals);
      })
      .catch(() => {});
  }, [costCenterNumber]);

  const monthOptions = (() => {
    const startIdx = forecastDim ? (DIMENSION_START_MONTH[forecastDim] ?? 0) : 0;
    return ALL_MONTH_OPTIONS.filter(o => o.value > startIdx);
  })();
  const firstAllowedMonth = monthOptions[0]?.value ?? 1;

  const addExisting = () => setExisting(prev => [...prev, emptyContractor(costCenterNumber)]);
  const addNewHire  = () => setNewHires(prev => [...prev, emptyNewContractor(costCenterNumber, firstAllowedMonth)]);

  const removeExisting = (idx: number) => setExisting(prev => prev.filter((_, i) => i !== idx));
  const removeNewHire  = (idx: number) => setNewHires(prev => prev.filter((_, i) => i !== idx));

  const requestRemove = (section: "existing" | "newHire", idx: number, name: string) =>
    setDeleteConfirm({ section, idx, name: name || (section === "newHire" ? "this new contractor" : "this contractor") });

  const confirmRemove = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.section === "existing") removeExisting(deleteConfirm.idx);
    else removeNewHire(deleteConfirm.idx);
    setDeleteConfirm(null);
  };

  const updateExisting = (idx: number, field: keyof Employee, value: string | number) =>
    setExisting(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  const updateNewHire  = (idx: number, field: keyof Employee, value: string | number) =>
    setNewHires(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const handleDrop = (targetSection: "existing" | "newHire") => {
    if (!dragSource || dragSource.section === targetSection) {
      setDragSource(null);
      setDropTarget(null);
      return;
    }
    if (dragSource.section === "existing") {
      const emp = existing[dragSource.idx];
      if (emp) {
        setExisting(prev => prev.filter((_, i) => i !== dragSource.idx));
        setNewHires(prev => [...prev, { ...emp, is_new_hire: true, hire_month: emp.hire_month || firstAllowedMonth }]);
      }
    } else {
      const emp = newHires[dragSource.idx];
      if (emp) {
        setNewHires(prev => prev.filter((_, i) => i !== dragSource.idx));
        setExisting(prev => [...prev, { ...emp, is_new_hire: false }]);
      }
    }
    setDragSource(null);
    setDropTarget(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const all = [
      ...existing.map(e => ({ ...e, is_new_hire: false })),
      ...newHires.map(e => ({ ...e, is_new_hire: true })),
    ];
    try {
      await api.post("/salary/employees", {
        cost_center: costCenterNumber,
        temp_labor_only: true,
        employees: all.map(e => ({
          employee_name:      e.employee_name,
          job_title:          e.job_title,
          annual_salary:      e.annual_salary,
          employment_type:    e.employment_type,
          bip_pct:            e.bip_pct,
          hire_month:         e.hire_month,
          status:             e.status || "active",
          termination_month:  e.termination_month || 0,
          contract_end_month: e.contract_end_month || 0,
          hours_per_week:     e.hours_per_week || 0,
          is_new_hire:        e.is_new_hire || false,
        })),
      });
      setFeedback({ ok: true, msg: "Saved successfully." });
      load();
      onDataSaved?.();
    } catch {
      setFeedback({ ok: false, msg: "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const getContractType = (emp: Employee): ContractType => emp.hours_per_week > 0 ? "Hourly" : "Turnkey";

  // Compute monthly values
  const existingWithMonthly = existing.map(emp => ({
    ...emp,
    monthly: computeMonthly(emp.annual_salary, emp.hire_month, emp.contract_end_month, emp.status, emp.termination_month, getContractType(emp)),
  }));
  const newHiresWithMonthly = newHires.map(emp => ({
    ...emp,
    monthly: computeMonthly(emp.annual_salary, emp.hire_month, emp.contract_end_month, emp.status, emp.termination_month, getContractType(emp)),
  }));

  const allWithMonthly = [...existingWithMonthly, ...newHiresWithMonthly];

  const monthlyTotals: Record<string, number> = {};
  MONTHS.forEach(m => { monthlyTotals[m] = 0; });
  allWithMonthly.forEach(emp => {
    MONTHS.forEach(m => { monthlyTotals[m] += emp.monthly[m]; });
  });
  const annualTotal = MONTHS.reduce((s, m) => s + monthlyTotals[m], 0);

  const editingDisabled = false;

  if (loading) return <div className="salary-loading">Loading contractor data…</div>;

  // Column count: name + title + hourly + hrs/wk + start + end + status + term + 12 months + total + del = 21
  const colCount = 22;

  const contractorTableBody = (
    rows: typeof existingWithMonthly,
    onUpdate: (idx: number, field: keyof Employee, value: string | number) => void,
    section: "existing" | "newHire",
    isNewHire: boolean
  ) => rows.map((emp, idx) => (
    <tr
      key={idx}
      className={`salary-row${emp.status === "terminated" ? " salary-row--terminated" : ""}${dragSource?.section === section && dragSource?.idx === idx ? " salary-row--dragging" : ""}`}
      draggable={!editingDisabled}
      onDragStart={e => {
        setDragSource({ section, idx });
        e.dataTransfer.effectAllowed = "move";
        if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = "0.4";
      }}
      onDragEnd={e => {
        setDragSource(null);
        setDropTarget(null);
        if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = "";
      }}
    >
      <td className="col-name">
        <input
          className="salary-input"
          value={emp.employee_name}
          placeholder={isNewHire ? "Planned" : "Contractor name (optional)"}
          onChange={e => onUpdate(idx, "employee_name", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-title">
        <input
          className="salary-input"
          value={emp.job_title}
          placeholder="Role / description"
          onChange={e => onUpdate(idx, "job_title", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-type">
        <select
          className="salary-chip-select salary-chip--type"
          value={getContractType(emp)}
          onChange={e => {
            if (e.target.value === "Turnkey") {
              onUpdate(idx, "hours_per_week", 0);
            } else {
              onUpdate(idx, "hours_per_week", 40);
              onUpdate(idx, "annual_salary", toAnnual(0, 40));
            }
          }}
          disabled={editingDisabled}
        >
          <option value="Hourly">Hourly</option>
          <option value="Turnkey">Turnkey</option>
        </select>
      </td>
      {getContractType(emp) === "Hourly" ? (
        <>
          <td className="col-hourly">
            <input
              className="salary-input salary-input--num"
              type="number" min={0} step={1}
              value={getHourlyRate(emp) || ""}
              placeholder="0"
              onChange={e => {
                const rate = parseFloat(e.target.value) || 0;
                onUpdate(idx, "annual_salary", toAnnual(rate, emp.hours_per_week || 40));
              }}
              disabled={editingDisabled}
            />
          </td>
          <td className="col-hpw">
            <input
              className="salary-input salary-input--num"
              type="number" min={0} max={80} step={1}
              value={emp.hours_per_week || ""}
              placeholder="40"
              onChange={e => {
                const hpw = parseFloat(e.target.value) || 0;
                onUpdate(idx, "hours_per_week", hpw);
                const currentRate = getHourlyRate(emp);
                onUpdate(idx, "annual_salary", toAnnual(currentRate, hpw));
              }}
              disabled={editingDisabled}
            />
          </td>
        </>
      ) : (
        <td className="col-contract-val" colSpan={2}>
          <input
            className="salary-input salary-input--num"
            type="number" min={0} step={1000}
            value={emp.annual_salary || ""}
            placeholder="Total contract $"
            onChange={e => onUpdate(idx, "annual_salary", parseFloat(e.target.value) || 0)}
            disabled={editingDisabled}
          />
        </td>
      )}
      {/* Contract Start */}
      <td className="col-hire-month">
        <select
          className="salary-chip-select salary-chip--month"
          value={emp.hire_month}
          onChange={e => {
            const start = parseInt(e.target.value);
            onUpdate(idx, "hire_month", start);
            if (start === 0) {
              onUpdate(idx, "contract_end_month", 0);
            } else if (emp.contract_end_month > 0 && emp.contract_end_month < start) {
              onUpdate(idx, "contract_end_month", start);
            }
            if (emp.termination_month > 0 && (emp.termination_month < start || (emp.contract_end_month > 0 && emp.termination_month > emp.contract_end_month))) {
              onUpdate(idx, "termination_month", 0);
            }
          }}
          disabled={editingDisabled}
        >
          {!isNewHire && <option value={0}>Full Year</option>}
          {ALL_MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </td>
      {/* Contract End */}
      <td className="col-end-month">
        {emp.hire_month === 0 ? (
          <span className="salary-cell-muted">–</span>
        ) : (
          <select
            className="salary-chip-select salary-chip--month"
            value={emp.contract_end_month}
            onChange={e => {
              const end = parseInt(e.target.value);
              onUpdate(idx, "contract_end_month", end);
              if (emp.termination_month > 0 && end > 0 && emp.termination_month > end) {
                onUpdate(idx, "termination_month", 0);
              }
            }}
            disabled={editingDisabled}
          >
            <option value={0}>Open</option>
            {ALL_MONTH_OPTIONS.filter(o => o.value >= emp.hire_month).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </td>
      {/* Status */}
      <td className="col-status">
        {isNewHire ? (
          <span className="salary-chip-select salary-chip--planned-label">Planned</span>
        ) : (
          <select
            className={`salary-chip-select salary-chip--status${emp.status === "terminated" ? " salary-chip--danger" : " salary-chip--ok"}`}
            value={emp.status || "active"}
            onChange={e => {
              onUpdate(idx, "status", e.target.value as Employee["status"]);
              if (e.target.value === "active") onUpdate(idx, "termination_month", 0);
            }}
            disabled={editingDisabled}
          >
            <option value="active">Active</option>
            <option value="terminated">Inactive/Moved</option>
          </select>
        )}
      </td>
      {/* Termination Month — only when terminated */}
      <td className="col-end-month">
        {!isNewHire && emp.status === "terminated" ? (
          <select
            className="salary-chip-select salary-chip--danger"
            value={emp.termination_month || 0}
            onChange={e => onUpdate(idx, "termination_month", parseInt(e.target.value))}
            disabled={editingDisabled}
          >
            <option value={0}>Select…</option>
            {ALL_MONTH_OPTIONS
              .filter(o => o.value >= (emp.hire_month || 1) && (emp.contract_end_month === 0 || o.value <= emp.contract_end_month))
              .map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <span className="salary-cell-muted">–</span>
        )}
      </td>
      {MONTHS.map(m => (
        <td key={m} className="col-month salary-calc-cell">
          {emp.monthly[m] > 0 ? fmt(emp.monthly[m]) : <span className="salary-cell-muted">–</span>}
        </td>
      ))}
      <td className="col-total salary-calc-cell salary-row-total">
        {fmt(MONTHS.reduce((s, m) => s + emp.monthly[m], 0))}
      </td>
      <td className="col-del">
        <button className="salary-del-btn" onClick={() => requestRemove(section, idx, emp.employee_name)} title="Remove" disabled={editingDisabled}>
          <span className="material-icons">delete_outline</span>
        </button>
      </td>
    </tr>
  ));

  return (
    <div className="salary-view">
      {editingDisabled && (
        <div className="salary-locked-banner">
          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 6 }}>lock</span>
          Contractor planning is currently disabled. Contact the Finance Admin to open forecast planning.
        </div>
      )}

      <div className="salary-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: "oklch(0.98 0.01 250)", background: "oklch(0.50 0.15 310)", padding: "3px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Planner</span>
          <span className="salary-header-sub-info">Contract / Outside Services</span>
          <span style={{ fontSize: 10, fontWeight: 600, color: "oklch(0.50 0.10 70)", background: "oklch(0.96 0.03 70)", border: "1px solid oklch(0.88 0.06 70)", padding: "2px 8px", borderRadius: 4 }}>Planning Only &mdash; not included in budget</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isDirty && !saving && (
            <span className="salary-unsaved-banner">
              <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }}>warning_amber</span>
              You have unsaved changes — remember to save
            </span>
          )}
          {feedback && (
            <span className={`salary-feedback ${feedback.ok ? "salary-feedback--ok" : "salary-feedback--err"}`}>
              {feedback.msg}
            </span>
          )}
          <button
            className={`salary-save-btn${isDirty ? " salary-save-btn--active" : ""}`}
            onClick={handleSave}
            disabled={saving || !isDirty || editingDisabled}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="salary-table-wrap">
        <table className="salary-table">
          <thead>
            <tr className="salary-thead-top">
              <th className="col-name">Contractor Name</th>
              <th className="col-title">Role / Description</th>
              <th className="col-type">Type</th>
              <th className="col-hourly">Rate / Value</th>
              <th className="col-hpw">Hrs/Wk</th>
              <th className="col-hire-month">Start Mo.</th>
              <th className="col-end-month">End Mo.</th>
              <th className="col-status">Status</th>
              <th className="col-end-month">Term. Mo.</th>
              {MONTHS.map(m => <th key={m} className="col-month">{MONTH_ABBR[m]}</th>)}
              <th className="col-total">Total FY</th>
              <th className="col-del" />
            </tr>
          </thead>
          <tbody>
            {/* Existing Contractors */}
            <tr
              className={`salary-section-header-row${dropTarget === "existing" && dragSource?.section === "newHire" ? " salary-section--drop-active" : ""}`}
              onDragOver={e => { if (dragSource?.section === "newHire") { e.preventDefault(); setDropTarget("existing"); } }}
              onDragLeave={() => { if (dropTarget === "existing") setDropTarget(null); }}
              onDrop={e => { e.preventDefault(); handleDrop("existing"); }}
            >
              <td colSpan={colCount}>
                <div className="salary-section-bar">
                  <div className="salary-section-bar-left">
                    <span className="salary-section-title">Current Contractors</span>
                    <span className="salary-section-count">{existing.length}</span>
                    {dropTarget === "existing" && dragSource?.section === "newHire" && (
                      <span className="salary-drop-hint">
                        <span className="material-icons">arrow_downward</span>
                        Drop to move here
                      </span>
                    )}
                  </div>
                  <button className="salary-section-add-btn" onClick={addExisting} disabled={editingDisabled}>
                    <span className="material-icons">add</span>
                    Add Contractor
                  </button>
                </div>
              </td>
            </tr>
            {existing.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="salary-empty-row">
                  No contractors added yet.
                </td>
              </tr>
            ) : (
              contractorTableBody(existingWithMonthly, updateExisting, "existing", false)
            )}

            {/* Budget New Contractors */}
            <tr
              className={`salary-section-header-row salary-newhire-header${dropTarget === "newHire" && dragSource?.section === "existing" ? " salary-section--drop-active" : ""}`}
              onDragOver={e => { if (dragSource?.section === "existing") { e.preventDefault(); setDropTarget("newHire"); } }}
              onDragLeave={() => { if (dropTarget === "newHire") setDropTarget(null); }}
              onDrop={e => { e.preventDefault(); handleDrop("newHire"); }}
            >
              <td colSpan={colCount}>
                <div className="salary-section-bar">
                  <div className="salary-section-bar-left">
                    <span className="salary-section-title">Planned New Contractors</span>
                    <span className="salary-section-count salary-section-count--nh">{newHires.length}</span>
                    {dropTarget === "newHire" && dragSource?.section === "existing" && (
                      <span className="salary-drop-hint">
                        <span className="material-icons">arrow_downward</span>
                        Drop to move here
                      </span>
                    )}
                  </div>
                  <button className="salary-section-add-btn salary-section-add-btn--newhire" onClick={addNewHire} disabled={editingDisabled}>
                    <span className="material-icons">add</span>
                    Plan New Contractor
                  </button>
                </div>
              </td>
            </tr>
            {newHires.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="salary-empty-row">
                  No new contractors planned.
                </td>
              </tr>
            ) : (
              contractorTableBody(newHiresWithMonthly, updateNewHire, "newHire", true)
            )}
          </tbody>

          <tfoot>
            <tr className="salary-tfoot-spacer"><td colSpan={colCount} /></tr>
            <tr className="salary-summary-row salary-total-row">
              <td colSpan={9} className="salary-summary-label">Total Contract / Outside Services</td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">
                  {fmt(monthlyTotals[m])}
                </td>
              ))}
              <td className="col-total salary-calc-cell salary-row-total">
                {fmt(annualTotal)}
              </td>
              <td className="col-del" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Spend Plan Chart */}
      {(() => {
        const actualsAnnual = MONTHS.reduce((s, m) => s + (actualsMonthly[m] || 0), 0);
        const hasActuals = actualsAnnual > 0;
        const ytdMonths = MONTHS.filter(m => actualsMonthly[m] !== undefined && actualsMonthly[m] !== 0);
        const ytdActual = ytdMonths.reduce((s, m) => s + (actualsMonthly[m] || 0), 0);
        const ytdPlanned = ytdMonths.reduce((s, m) => s + (monthlyTotals[m] || 0), 0);
        const ytdVariance = ytdPlanned - ytdActual;
        const chartData = MONTHS.map(m => {
          const hasMonth = actualsMonthly[m] !== undefined && actualsMonthly[m] !== 0;
          return {
            name: MONTH_ABBR[m],
            mpy: hasMonth ? (actualsMonthly[m] || 0) : monthlyTotals[m],
            planned: monthlyTotals[m],
          };
        });
        let cumMpy = 0, cumPlanned = 0;
        const cumData = MONTHS.map(m => {
          const hasMonth = actualsMonthly[m] !== undefined && actualsMonthly[m] !== 0;
          cumMpy += hasMonth ? (actualsMonthly[m] || 0) : monthlyTotals[m];
          cumPlanned += monthlyTotals[m];
          return { name: MONTH_ABBR[m], cMpy: cumMpy, cPlanned: cumPlanned };
        });
        const hasData = annualTotal > 0 || hasActuals;
        const fmtAxis = (v: number) => v === 0 ? "$0" : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`;
        const fmtLabel = (v: any) => { const n = Number(v); if (!n) return ""; return n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}k` : `$${Math.round(n)}`; };
        const fmtTip = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
        const CustomTooltip = ({ active, payload, label }: any) => {
          if (!active || !payload?.length) return null;
          return (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
              {payload.map((entry: any, i: number) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color }} />
                  <span>{entry.name}:</span>
                  <span style={{ fontWeight: 600 }}>{fmtTip(entry.value)}</span>
                </div>
              ))}
            </div>
          );
        };
        return (
          <div style={{ background: "#fff", border: "1.5px solid oklch(0.9 0.01 250)", borderRadius: 10, marginTop: 14, marginBottom: 14, overflow: "hidden" }}>
            <div
              onClick={() => setTempChartOpen(!tempChartOpen)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", cursor: "pointer", background: "oklch(0.97 0.005 250)", borderBottom: tempChartOpen ? "1px solid oklch(0.92 0.01 250)" : "none", userSelect: "none" }}
            >
              <span className="material-icons" style={{ fontSize: 18, color: "#0891b2" }}>bar_chart</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "oklch(0.25 0.02 250)", textTransform: "uppercase", letterSpacing: "0.03em" }}>Contract / Outside Services Spend Plan</span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <span>{annualTotal > 0 ? `Plan: $${Math.round(annualTotal).toLocaleString()}` : "No plan yet"}</span>
                {hasActuals && (
                  <>
                    <span>·</span>
                    <span>YTD Actual: <span style={{ fontWeight: 600, color: "oklch(0.55 0.15 70)" }}>${Math.round(ytdActual).toLocaleString()}</span></span>
                    <span>·</span>
                    <span>YTD Variance: <span style={{ fontWeight: 600, color: ytdVariance >= 0 ? "oklch(0.45 0.15 145)" : "oklch(0.50 0.18 25)" }}>{ytdVariance < 0 ? `($${Math.round(Math.abs(ytdVariance)).toLocaleString()})` : `$${Math.round(ytdVariance).toLocaleString()}`}</span></span>
                  </>
                )}
              </span>
              {!tempChartOpen && (
                <span style={{ marginLeft: "auto", fontSize: 11, color: "oklch(0.45 0.10 250)", fontWeight: 600, letterSpacing: "0.02em" }}>Click to expand</span>
              )}
              <span className="material-icons" style={{ marginLeft: tempChartOpen ? "auto" : 6, fontSize: 20, color: "#64748b" }}>
                {tempChartOpen ? "expand_less" : "expand_more"}
              </span>
            </div>
            {tempChartOpen && (
              <div style={{ padding: "12px 16px 16px" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <div style={{ display: "inline-flex", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                    <button
                      onClick={() => setTempChartType("bar")}
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", fontSize: 12, fontWeight: tempChartType === "bar" ? 700 : 500, background: tempChartType === "bar" ? "oklch(0.25 0.04 250)" : "#fff", color: tempChartType === "bar" ? "#fff" : "#64748b", border: "none", cursor: "pointer" }}
                    >
                      <span className="material-icons" style={{ fontSize: 14 }}>bar_chart</span> Bar
                    </button>
                    <button
                      onClick={() => setTempChartType("line")}
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", fontSize: 12, fontWeight: tempChartType === "line" ? 700 : 500, background: tempChartType === "line" ? "oklch(0.25 0.04 250)" : "#fff", color: tempChartType === "line" ? "#fff" : "#64748b", border: "none", cursor: "pointer" }}
                    >
                      <span className="material-icons" style={{ fontSize: 14 }}>show_chart</span> Line
                    </button>
                  </div>
                </div>
                {hasData ? (
                  <ResponsiveContainer width="100%" height={220}>
                    {tempChartType === "bar" ? (
                      <ComposedChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                        <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "oklch(0.97 0.005 260)" }} />
                        <Legend />
                        <Bar dataKey="mpy" name="MPY" fill="#0891b2" radius={[3, 3, 0, 0]}>
                          <LabelList dataKey="mpy" position="top" formatter={fmtLabel} style={{ fontSize: 10, fill: "#0891b2", fontWeight: 600 }} />
                        </Bar>
                        <Line type="monotone" dataKey="planned" name="Planned" stroke="#4A90E2" strokeWidth={2} dot={{ r: 3, fill: "#4A90E2", strokeWidth: 1.5, stroke: "#fff" }} />
                      </ComposedChart>
                    ) : (
                      <LineChart data={cumData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                        <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="cMpy" name="MPY (Cumulative)" stroke="#0891b2" strokeWidth={2.5} dot={{ r: 3, fill: "#0891b2", strokeWidth: 1.5, stroke: "#fff" }} />
                        <Line type="monotone" dataKey="cPlanned" name="Planned (Cumulative)" stroke="#4A90E2" strokeWidth={2} dot={false} strokeDasharray="6 3" />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 13 }}>
                    Add contractors to see the spend plan chart.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Note */}
      <div className="salary-note">
        <span className="material-icons salary-note-icon">info_outline</span>
        <div className="salary-note-content">
          <p><strong>Planning only:</strong> Contract and outside services entries are for planning purposes and are not included in the budget or forecast dimensions.</p>
          <p><strong>Contract type:</strong> Choose <em>Hourly</em> for time-based contractors (Monthly = Hourly Rate x Hrs/Wk x 52 / 12) or <em>Turnkey</em> for fixed-price engagements (total contract value spread evenly across active months).</p>
          <p><strong>Start / End:</strong> Define the contract period. Cost is applied only within the start and end months. Use "Full Year" for either end if open-ended.</p>
          <p><strong>Status &amp; Term month:</strong> If a contractor leaves early (before the contract end), set Status to "Inactive/Moved" and select the last paid month. Cost stops after that month.</p>
        </div>
      </div>


      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="salary-delete-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="salary-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="salary-delete-modal-icon">
              <span className="material-icons">warning_amber</span>
            </div>
            <div className="salary-delete-modal-title">Remove Contractor</div>
            <div className="salary-delete-modal-msg">
              Are you sure you want to remove <strong>{deleteConfirm.name}</strong>?
            </div>
            <div className="salary-delete-modal-actions">
              <button className="salary-delete-modal-btn salary-delete-modal-btn--cancel" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="salary-delete-modal-btn salary-delete-modal-btn--confirm" onClick={confirmRemove}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TempLaborView;
